import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Chip,
	Container,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	Typography,
} from '@material-ui/core';
import EditIcon from '@mui/icons-material/Edit';
import { DriverServiceUrl, endpoints } from 'utils/factory';

const headCells = [
	{ id: 'sno', label: '#', sortable: false },
	{ id: 'full_name', label: 'Driver Name' },
	{ id: 'contact_no', label: 'Contact No.' },
	{ id: 'email', label: 'Email' },
	{ id: 'location', label: 'Location', sortable: false },
	{ id: 'license_no', label: 'License No.' },
	{ id: 'vehicle_no', label: 'Vehicle No.' },
	{ id: 'status', label: 'Status' },
	{ id: 'action', label: 'Action', sortable: false },
];

const statusOptions = [
	{ label: 'All', value: 'all' },
	{ label: 'Active', value: '1' },
	{ label: 'Inactive', value: '0' },
];

const rowsPerPageOptions = [5, 10, 25, 50];

const normalizeText = (value) => String(value ?? '').trim().toLowerCase();

const getLocation = (driver) => [driver?.street, driver?.city, driver?.state, driver?.country].filter(Boolean).join(', ');

const getStatusLabel = (status) => {
	if (status === 1 || status === '1') {
		return 'Active';
	}

	if (status === 0 || status === '0') {
		return 'Inactive';
	}

	return 'Unknown';
};

const getStatusSortValue = (status) => {
	if (status === 1 || status === '1') {
		return 1;
	}

	if (status === 0 || status === '0') {
		return 0;
	}

	return -1;
};

const descendingComparator = (a, b, orderBy) => {
	const aValue = orderBy === 'location' ? getLocation(a) : a?.[orderBy];
	const bValue = orderBy === 'location' ? getLocation(b) : b?.[orderBy];

	if (orderBy === 'status') {
		return getStatusSortValue(bValue) - getStatusSortValue(aValue);
	}

	if (bValue < aValue) {
		return -1;
	}

	if (bValue > aValue) {
		return 1;
	}

	return 0;
};

const getComparator = (order, orderBy) => (order === 'desc'
	? (a, b) => descendingComparator(a, b, orderBy)
	: (a, b) => -descendingComparator(a, b, orderBy));

const stableSort = (array, comparator) => {
	const stabilized = array.map((el, index) => [el, index]);

	stabilized.sort((a, b) => {
		const order = comparator(a[0], b[0]);

		if (order !== 0) {
			return order;
		}

		return a[1] - b[1];
	});

	return stabilized.map((el) => el[0]);
};

const DriverList = () => {
	const [drivers, setDrivers] = useState([]);
	const [submitError, setSubmitError] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();
	const [searchText, setSearchText] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [order, setOrder] = useState('asc');
	const [orderBy, setOrderBy] = useState('full_name');
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const fetchDrivers = async () => {
		try {
			setIsLoading(true);
			setSubmitError('');

			const response = await axios.get(
				`${DriverServiceUrl}/${endpoints.fetchDrivers}?delivery_comp_id=${localStorage.getItem('trans-userId')}`
			);
			const responseData = response?.data?.data;

			if (response?.data?.status === false || responseData?.status === false) {
				setSubmitError(response?.data?.message || responseData?.message || 'Failed to fetch drivers.');
				setDrivers([]);
				return;
			}

			const data = Array.isArray(responseData)
				? responseData
				: Array.isArray(response?.data)
					? response.data
					: [];

			setDrivers(data);
		} catch (error) {
			const apiMessage = error?.response?.data?.message;
			setSubmitError(apiMessage || 'Database error occurred. Please try again later.');
			setDrivers([]);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchDrivers();
	}, []);

	useEffect(() => {
		setPage(0);
	}, [searchText, statusFilter, order, orderBy]);

	const filteredDrivers = useMemo(() => {
		const query = normalizeText(searchText);

		return drivers.filter((driver) => {
			const matchesStatus = statusFilter === 'all' || String(driver?.status ?? '') === statusFilter;
			const searchableFields = [
				driver?.sno,
				driver?.full_name,
				driver?.email,
				driver?.contact_no,
				driver?.street,
				driver?.city,
				driver?.state,
				driver?.country,
				driver?.license_no,
				driver?.vehicle_no,
				getStatusLabel(driver?.status),
			]
				.map(normalizeText)
				.join(' ');

			const matchesSearch = !query || searchableFields.includes(query);

			return matchesStatus && matchesSearch;
		});
	}, [drivers, searchText, statusFilter]);

	const sortedDrivers = useMemo(
		() => stableSort(filteredDrivers, getComparator(order, orderBy)),
		[filteredDrivers, order, orderBy]
	);

	const paginatedDrivers = useMemo(
		() => sortedDrivers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
		[page, rowsPerPage, sortedDrivers]
	);

	const handleRequestSort = (property) => {
		const isAsc = orderBy === property && order === 'asc';

		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleDriverRegistration = () => {
		navigate('/app/driver-registration');
	};

	const handleEdit = (driverId) => {
		navigate(`/app/driver-registration?driver=${driverId}`);
	};

	return (
		<>
			<Helmet>
				<title>Driver List</title>
			</Helmet>

			<Box
				sx={{
					backgroundColor: 'background.default',
					minHeight: '100%',
					py: 4,
				}}
			>
				<Container maxWidth="xl">
					<Paper
						elevation={2}
						style={{
							borderRadius: 16,
							overflow: 'hidden',
						}}
					>
						<Box
							sx={{
								background: 'linear-gradient(135deg, #000000 0%, #0f0f0f 40%, #1c1c1c 100%)',
								color: '#fff',
								px: 3,
								py: 3,
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: { xs: 'column', md: 'row' },
									alignItems: { xs: 'flex-start', md: 'center' },
									justifyContent: 'space-between',
									gap: 2,
								}}
							>
								<Box>
									<Typography color="inherit" variant="h4">
										Driver List
									</Typography>
									<Typography color="inherit" variant="body2" style={{ opacity: 0.85, marginTop: 8 }}>
										Browse, filter, sort, and paginate the drivers loaded from the delivery service.
									</Typography>
								</Box>

								<Button
									color="secondary"
									variant="contained"
									onClick={handleDriverRegistration}
									style={{
										backgroundColor: '#ffffff',
										color: '#111827',
										fontWeight: 700,
										whiteSpace: 'nowrap',
									}}
								>
									+ Driver Registration
								</Button>
							</Box>
						</Box>

						<Box sx={{ p: 3 }}>
							<Grid container spacing={2} style={{ marginBottom: 16 }}>
								<Grid item xs={12} md={6}>
									<TextField
										fullWidth
										label="Search drivers"
										placeholder="Name, email, contact, license, vehicle..."
										variant="outlined"
										value={searchText}
										onChange={(event) => setSearchText(event.target.value)}
									/>
								</Grid>
								<Grid item xs={12} md={3}>
									<FormControl variant="outlined" fullWidth>
										<InputLabel>Status</InputLabel>
										<Select
											label="Status"
											value={statusFilter}
											onChange={(event) => setStatusFilter(event.target.value)}
										>
											{statusOptions.map((option) => (
												<MenuItem key={option.value} value={option.value}>
													{option.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
								<Grid item xs={12} md={3}>
									<TextField
										fullWidth
										label="Rows per page"
										select
										variant="outlined"
										value={rowsPerPage}
										onChange={handleChangeRowsPerPage}
									>
										{rowsPerPageOptions.map((option) => (
											<MenuItem key={option} value={option}>
												{option}
											</MenuItem>
										))}
									</TextField>
								</Grid>
							</Grid>

							{submitError ? (
								<Alert severity="error" style={{ marginBottom: 16 }}>
									{submitError}
								</Alert>
							) : null}

							<TableContainer style={{ border: '1px solid #e5e7eb', borderRadius: 12 }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											{headCells.map((headCell) => (
												<TableCell key={headCell.id} style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
													{headCell.sortable === false ? (
														headCell.label
													) : (
														<TableSortLabel
															active={orderBy === headCell.id}
															direction={orderBy === headCell.id ? order : 'asc'}
															onClick={() => handleRequestSort(headCell.id)}
														>
															{headCell.label}
														</TableSortLabel>
													)}
												</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{isLoading ? (
											<TableRow>
												<TableCell colSpan={headCells.length} align="center" style={{ padding: 32 }}>
													<Typography variant="body1">Loading drivers...</Typography>
												</TableCell>
											</TableRow>
										) : paginatedDrivers.length > 0 ? (
											paginatedDrivers.map((driver, index) => (
												<TableRow hover key={driver?.driver_id || driver?.sno || `${driver?.full_name}-${index}`}>
													<TableCell>{page * rowsPerPage + index + 1}</TableCell>
													<TableCell>{driver?.full_name || '-'}</TableCell>
													<TableCell>{driver?.contact_no || '-'}</TableCell>
													<TableCell>{driver?.email || '-'}</TableCell>
													<TableCell>{getLocation(driver) || '-'}</TableCell>
													<TableCell>{driver?.license_no || '-'}</TableCell>
													<TableCell>{driver?.vehicle_no || '-'}</TableCell>
													<TableCell>
														<Chip
															size="small"
															label={getStatusLabel(driver?.status)}
															style={{
																backgroundColor: getStatusSortValue(driver?.status) === 1 ? '#e7f7ed' : '#fbe9e7',
																color: getStatusSortValue(driver?.status) === 1 ? '#146c2e' : '#c62828',
																fontWeight: 600,
															}}
														/>
													</TableCell>
													<TableCell>
														<IconButton
															size="small"
															color="primary"
															onClick={() => handleEdit(driver?.driver_id)}
														>
															<EditIcon fontSize="small" />
														</IconButton>
													</TableCell>
												</TableRow>
											))
										) : (
											<TableRow>
												<TableCell colSpan={headCells.length} align="center" style={{ padding: 32 }}>
													<Typography variant="body1">No drivers found.</Typography>
													<Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
														Try adjusting the search or status filter.
													</Typography>
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>

							<TablePagination
								component="div"
								count={filteredDrivers.length}
								page={page}
								onPageChange={handleChangePage}
								onRowsPerPageChange={handleChangeRowsPerPage}
								rowsPerPage={rowsPerPage}
								rowsPerPageOptions={rowsPerPageOptions}
							/>
						</Box>
					</Paper>
				</Container>
			</Box>
		</>
	);
};

export default DriverList;
